import { invoicesService } from '@/api/invoices.service';
import type {
  CreateInvoiceDto,
  ExportInvoicesCsvDto,
  FindAllInvoicesQueryDto,
  Invoice,
  UpdateInvoiceDto,
} from '@/entities/invoices/invoice.entity';
import { InvoiceStatus } from '@/entities/invoices/invoice-status.enum';
import {
  addItemToQueriesData,
  removeItemFromQueriesData,
  updateItemOnQueriesData,
} from '@/utils/update-query-data';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRefOrGetter, toValue } from 'vue';
import type { MutationDefaultsContext } from './query-client';
import type {
  CreateInvoiceFromLedgerEntriesDto,
  InvoicingRunResultDto,
} from '@carvia/ros-client-types';

export const INVOICES_QUERY_KEY = 'invoices';

export const useInvoice = (id: MaybeRefOrGetter<string>) => {
  return useQuery({
    queryKey: [INVOICES_QUERY_KEY, id],
    queryFn: () => invoicesService.getOne(toValue(id)),
  });
};

export const useInvoices = () => {
  return useQuery({
    queryKey: [INVOICES_QUERY_KEY],
    queryFn: () => invoicesService.getAll(),
  });
};

export const useInvoicesWithQuery = (
  query: MaybeRefOrGetter<FindAllInvoicesQueryDto>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: [INVOICES_QUERY_KEY, query],
    queryFn: ({ pageParam = 1 }) =>
      invoicesService.getAllWithQuery({
        ...toValue(query),
        page: pageParam,
      }),
    enabled: toValue(enabled),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });
};

export const useUpdateInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [INVOICES_QUERY_KEY],
    mutationFn: ({ id, invoice }: { id: string; invoice: UpdateInvoiceDto }) =>
      invoicesService.update(id, invoice),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Invoice updated',
    }),
    onSuccess: async (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      updateItemOnQueriesData<Invoice>([INVOICES_QUERY_KEY], result, queryClient);
      if (result.booking?.id) {
        await queryClient.invalidateQueries({ queryKey: ['bookings', result.booking.id] });
      }
      if (variables.invoice?.status === InvoiceStatus.CANCELED) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['ledger-entries'] }),
          queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] }),
        ]);
      }
    },
  });
};

export const useCreateInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [INVOICES_QUERY_KEY],
    mutationFn: (invoice: CreateInvoiceDto) => invoicesService.create(invoice),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Invoice saved',
    }),
    onSuccess: async (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      addItemToQueriesData<Invoice>([INVOICES_QUERY_KEY], result, queryClient);
      if (result.booking?.id) {
        await queryClient.invalidateQueries({ queryKey: ['bookings', result.booking.id] });
      }
    },
  });
};

export const useCreateInvoiceFromLedgerEntries = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [INVOICES_QUERY_KEY],
    mutationFn: (dto: CreateInvoiceFromLedgerEntriesDto) =>
      invoicesService.createFromLedgerEntries(dto),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Invoice saved',
      showSuccessToast: false,
    }),
    onSuccess: async (result: InvoicingRunResultDto, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      await queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      if (variables.bookingId) {
        await queryClient.invalidateQueries({ queryKey: ['bookings', variables.bookingId] });
      }
      await queryClient.invalidateQueries({ queryKey: ['ledger-entries'] });
    },
  });
};

export const useDeleteInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [INVOICES_QUERY_KEY],
    mutationFn: (id: string) => invoicesService.remove(id),
    onMutate: (id) => {
      return { id };
    },
    onSuccess: (result, variables, context) => {
      removeItemFromQueriesData([INVOICES_QUERY_KEY], context?.id, queryClient);
    },
  });
};

export const useSendInvoiceToCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [INVOICES_QUERY_KEY],
    mutationFn: (invoiceId: string) => invoicesService.sendInvoiceToCustomer(invoiceId),
    onMutate: () => ({
      successMessage: 'Invoice sent to customer successfully',
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [INVOICES_QUERY_KEY],
      });
    },
  });
};

export const useRegenerateInvoicePdf = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [INVOICES_QUERY_KEY],
    mutationFn: (invoiceId: string) => invoicesService.regenerateInvoicePdf(invoiceId),
    onMutate: () => ({
      successMessage: 'Invoice PDF generation queued',
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [INVOICES_QUERY_KEY],
      });
    },
  });
};

export const useExportInvoicesCsv = () => {
  return useMutation({
    mutationKey: ['invoices-export'],
    mutationFn: (query: ExportInvoicesCsvDto) => invoicesService.exportInvoicesCsv(query),
  });
};

export const useExportInvoicesPdfs = () => {
  return useMutation({
    mutationKey: ['invoices-export-pdfs'],
    mutationFn: (query: ExportInvoicesCsvDto) => invoicesService.exportInvoicesPdfs(query),
  });
};

export const useSendInvoiceToAgent = () => {
  return useMutation({
    mutationKey: [INVOICES_QUERY_KEY, 'agency'],
    mutationFn: (bookingId: string) => invoicesService.sendInvoiceToAgent(bookingId),
    onMutate: () => ({ successMessage: 'Invoice sent to agent' }),
  });
};
